<template>
  <div class="paper-mover">
    <div class="paper-mover__backdrop">
      <div class="paper-mover__modal">
        <!-- Header -->
        <header class="paper-mover__header">
          <h3 class="paper-mover__title">Move Paper</h3>
          <button
            type="button"
            class="paper-mover__close-btn"
            aria-label="Close modal"
            @click="paperMoverPopupClosed"
          >
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
        </header>

        <!-- Body -->
        <div class="paper-mover__body">
          <div class="paper-mover__tree-wrapper">
            <template v-if="bucketListResults.length > 0">
              <bucket-tree-view
                :tree-data="bucketListResults"
                :parent-directory="parentDirectory"
              ></bucket-tree-view>
            </template>
            <template v-else>
              <p class="paper-mover__empty-text">You don't have any shelf.</p>
            </template>
          </div>

          <!-- Apply Changes Button -->
          <div v-if="selectMoveFolderId" class="paper-mover__actions">
            <button
              type="button"
              class="paper-mover__btn-submit"
              :disabled="isSubmitted"
              @click="movePaperInParentFolder"
            >
              <span v-if="isSubmitted">Moving...</span>
              <span v-else>Apply Changes</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { bus } from '@/main';
import { mapGetters } from 'vuex';
import toast from '@/services/toast';
import BucketTreeView from './tree-view-bucket.vue';

export default {
  name: 'PaperMoveToFolder',
  components: {
    'bucket-tree-view': BucketTreeView
  },
  props: {
    paperMoverToFolderDATA: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectMoveFolderId: '',
      isSubmitted: false
    };
  },
  computed: {
    ...mapGetters(['getBucketListOfAwerackGetters']),
    bucketListResults() {
      const bucketData = this.getBucketListOfAwerackGetters;
      if (bucketData && bucketData.data && Array.isArray(bucketData.data.results)) {
        return bucketData.data.results;
      }
      return [];
    },
    parentDirectory() {
      return (this.paperMoverToFolderDATA && this.paperMoverToFolderDATA.parent_directory) || '';
    }
  },
  mounted() {
    bus.$on('getSelectFolderID', (data) => {
      this.selectMoveFolderId = data;
    });
  },
  beforeDestroy() {
    bus.$off('getSelectFolderID');
  },
  methods: {
    getPaperPileListAgain() {
      this.$store.dispatch('getAllPaperPileFun');
    },

    async movePaperInParentFolder() {
      if (!this.selectMoveFolderId) {
        toast.error('Please select a destination shelf');
        return;
      }

      this.isSubmitted = true;
      const payload = {
        parent: this.selectMoveFolderId,
        pdf_paper: this.paperMoverToFolderDATA.id,
        is_folder: false
      };

      try {
        const res = await this.$store.dispatch('createBucketOfAwerackDetailsFun', payload);
        const successMsg = (res && res.data && res.data.msg) || 'Paper moved successfully';
        toast.success(successMsg);

        this.getPaperPileListAgain();
        this.paperMoverPopupClosed();
      } catch (err) {
        const errorMsg =
          (err.response && err.response.data && (err.response.data.user_msg || err.response.data.msg)) ||
          (err.data && (err.data.user_msg || err.data.msg)) ||
          'Failed to move paper';
        toast.error(errorMsg);
        this.paperMoverPopupClosed();
      } finally {
        this.isSubmitted = false;
      }
    },

    paperMoverPopupClosed() {
      bus.$emit('paperMoverToFolderBus', false);
      document.body.classList.remove('body_right_scroll_remove');
    }
  }
};
</script>

<style lang="scss" scoped>
$font-stack: 'MyriadProRegular', 'Lato', sans-serif;
$theme-gold: #ffd400;
$accent-blue: #87cefa;
$bg-backdrop: rgba(0, 0, 0, 0.92);
$text-light: #ffffff;
$error-color: #dc3545;

.paper-mover {
  font-family: $font-stack;

  &__backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    background: $bg-backdrop;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
  }

  &__modal {
    background: #000000;
    width: 100%;
    max-width: 440px;
    border-radius: 8px;
    box-shadow: 0 0 16px rgba(255, 213, 0, 0.5);
    border: 1px solid rgba(255, 213, 0, 0.2);
    overflow: hidden;
  }

  &__header {
    padding: 14px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    color: $text-light;
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  &__close-btn {
    background: transparent;
    border: none;
    color: $error-color;
    font-size: 1.25rem;
    cursor: pointer;
    line-height: 1;
    padding: 0;
    transition: color 0.2s ease, transform 0.2s ease;

    &:hover {
      color: $accent-blue;
      transform: scale(1.1);
    }
  }

  &__body {
    padding: 20px;
  }

  &__tree-wrapper {
    max-height: 300px;
    overflow-y: auto;
    padding-right: 4px;

    &::-webkit-scrollbar {
      width: 4px;
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #333333;
      border-radius: 2px;
    }
  }

  &__empty-text {
    text-align: center;
    color: rgba(255, 255, 255, 0.6);
    margin: 20px 0;
    font-size: 0.95rem;
  }

  &__actions {
    margin-top: 20px;
    text-align: center;
  }

  &__btn-submit {
    width: 100%;
    padding: 10px 0;
    font-size: 1rem;
    font-weight: 600;
    font-family: inherit;
    color: $theme-gold;
    background: transparent;
    border: 2px solid $theme-gold;
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: $theme-gold;
      color: #22292f;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>